import type { Choice, Round, SubmitResult } from "./types";

/**
 * Decisions are queued in localStorage before they are sent, so a tap on a
 * phone with no signal is never lost. The round is the source of truth for
 * what was ASKED; this queue is the source of truth for what was CHOSEN until
 * the server acknowledges it.
 */
const QUEUE_KEY = "council.pending";

type Pending = Record<string, Choice>;

function readQueue(): Pending {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) ?? "{}") as Pending;
  } catch {
    return {};
  }
}

function writeQueue(q: Pending): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  } catch {
    /* private mode, quota - the in-memory state still works this session */
  }
}

export function queueDecision(candidateId: string, choice: Choice): void {
  const q = readQueue();
  q[candidateId] = choice;
  writeQueue(q);
}

export function pendingDecisions(): Pending {
  return readQueue();
}

/**
 * Drop queued taps that do not belong to the round now on screen.
 *
 * Without this, a submitted round's choices sit in localStorage forever and
 * get merged into every future round. They cannot collide today because
 * candidate ids carry a date, but relying on that is relying on an accident.
 */
export function pruneQueue(validIds: string[]): void {
  const keep = new Set(validIds);
  const q = readQueue();
  let changed = false;
  for (const id of Object.keys(q)) {
    if (!keep.has(id)) {
      delete q[id];
      changed = true;
    }
  }
  if (changed) writeQueue(q);
}

export function clearQueue(): void {
  writeQueue({});
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText}${body ? ` - ${body}` : ""}`);
  }
  return (await res.json()) as T;
}

export async function fetchRound(signal?: AbortSignal): Promise<Round | null> {
  const res = await fetch("/api/round", { signal, headers: { accept: "application/json" } });
  if (res.status === 204) return null;
  return json<Round>(res);
}

export async function submitRound(
  roundId: string,
  decisions: Record<string, Choice>,
): Promise<SubmitResult> {
  const res = await fetch("/api/round/submit", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ roundId, decisions }),
  });
  const out = await json<SubmitResult>(res);
  clearQueue();
  return out;
}
